import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, Check, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export function AssetLibrary({ kind, assets, selectedIds, onPick, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const list = assets.filter((a) => a.kind === kind);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('kind', kind);
      fd.append('name', f.name.replace(/\.[^.]+$/, ''));
      await axios.post(`${API}/api/admin/brand/library`, fd, { headers: auth() });
      toast.success(`${kind === 'logo' ? 'Logo' : 'Background'} added to library`);
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const remove = async (a, e) => {
    e.stopPropagation();
    if (!window.confirm(`Remove "${a.name}" from the library?`)) return;
    try {
      await axios.delete(`${API}/api/admin/brand/library/${a.id}`, { headers: auth() });
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`brand-library-${kind}`}>
      {list.map((a) => {
        const on = selectedIds.includes(a.id);
        return (
          <button key={a.id} onClick={() => onPick(a)} data-testid={`brand-asset-${a.id}`} title={a.name}
            className={`relative group aspect-[4/3] rounded-lg overflow-hidden border-2 transition-colors ${on ? 'border-orange-500' : 'border-border hover:border-orange-500/50'} ${kind === 'logo' ? 'bg-neutral-700' : 'bg-black'}`}>
            <img src={`${API}${a.url}`} alt={a.name} className={`w-full h-full ${kind === 'logo' ? 'object-contain p-1' : 'object-cover'}`} />
            {on && <span className="absolute top-1 left-1 bg-orange-600 text-black rounded-full p-0.5"><Check size={10} /></span>}
            {!a.is_default && (
              <span role="button" onClick={(e) => remove(a, e)} data-testid={`brand-asset-delete-${a.id}`}
                className="absolute top-1 right-1 bg-black/70 text-red-400 hover:text-red-300 rounded-full p-1"><Trash2 size={11} /></span>
            )}
            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white truncate px-1">{a.name}</span>
          </button>
        );
      })}
      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`brand-upload-${kind}`}
        className="aspect-[4/3] rounded-lg border-2 border-dashed border-border hover:border-orange-500/60 flex flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        Upload {kind}
      </button>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} />
    </div>
  );
}
